/**
 * NewToolsFieldWithMic – Rich text editor with toolbar, mic, image upload/capture, Save/Complete/Dispatch.
 * Matches admin4.0 CommonComponents NewToolsFieldWithMic for Histopathology report.
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatIndentIncreaseIcon from "@mui/icons-material/FormatIndentIncrease";
import FormatIndentDecreaseIcon from "@mui/icons-material/FormatIndentDecrease";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import TableChartIcon from "@mui/icons-material/TableChart";
import MicIcon from "@mui/icons-material/Mic";
import Divider from "@mui/material/Divider";
import VoiceToText from "./VoiceToText";
import OpenWebCamPopUP from "./OpenWebCamPopUP";

function removeEmptyStyleTagsAndReplaceSpan(content) {
  if (!content || content === "<br>") return "";
  const div = document.createElement("div");
  div.innerHTML = content;
  const elements = div.querySelectorAll("*[style]");
  elements.forEach((element) => {
    const style = element.getAttribute("style");
    const retainedStyles = (style || "")
      .split(";")
      .map((r) => r.trim())
      .filter((r) => {
        const [property, value] = r.split(":").map((s) => s?.trim());
        return property === "text-align" && ["center", "left", "right"].includes(value);
      })
      .join("; ");
    if (retainedStyles) element.setAttribute("style", retainedStyles);
    else element.removeAttribute("style");
  });
  div.querySelectorAll("span").forEach((span) => {
    const newDiv = document.createElement("div");
    Array.from(span.attributes).forEach((attr) => newDiv.setAttribute(attr.name, attr.value));
    while (span.firstChild) newDiv.appendChild(span.firstChild);
    span.parentNode.replaceChild(newDiv, span);
  });
  const tables = div.querySelectorAll("table");
  tables.forEach((table) => {
    table.setAttribute("style", "border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ccc;");
    table.querySelectorAll("td").forEach((td) => {
      td.setAttribute("style", "border: 1px solid #ccc; padding: 8px; vertical-align: top;");
    });
  });
  const firstDiv = div.firstElementChild;
  if (firstDiv && firstDiv.tagName.toLowerCase() === "div") {
    const style = firstDiv.getAttribute("style");
    if (style && style.includes("text-align")) {
      const alignValue = style.match(/text-align:\s*(left|right|center)/);
      firstDiv.setAttribute("style", alignValue ? alignValue[0] : "text-align: left;");
    } else {
      firstDiv.setAttribute("style", "text-align: left;");
    }
  }
  return div.innerHTML;
}

export default function NewToolsFieldWithMic({
  FromTestReport = false,
  selectedServiceData,
  EditorDisable = false,
  isDispatchshown = true,
  postButtonLabel = "Send Approval",
  showOnlyApproveButton = false,
  hideSaveAndPostButtons = false,
  viewOnly = false,
  Title = "Result",
  EditorRef = "",
  handleSaveResult,
  handlePostResult,
  handleDispatchResult,
  overallHeight = "78vh",
  height = "51.5vh",
  EditorHeight = "53vh",
  ResultPDF = [],
  micTextEditDisabled = false,
  addSpaceOnTab = true,
  errorMsg,
}) {
  const editorRef = useRef(null);
  const uploadPDFRef = useRef(null);
  const clickKeyRef = useRef(false);

  const status = selectedServiceData?.result_status ?? "";
  const isCompleted = status === "Completed";

  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [strike, setStrike] = useState(false);
  const [justifyLeft, setJustifyLeft] = useState(true);
  const [justifyCenter, setJustifyCenter] = useState(false);
  const [justifyRight, setJustifyRight] = useState(false);
  const [justifyFull, setJustifyFull] = useState(false);
  const [bulletList, setBulletList] = useState(false);
  const [orderedList, setOrderedList] = useState(false);
  const [tableMenuAnchor, setTableMenuAnchor] = useState(null);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [listenPopUp, setListenPopUp] = useState(false);
  const [pdfUploadList, setPdfUploadList] = useState(() => (Array.isArray(ResultPDF) ? [...ResultPDF] : []));
  const [capturedImg, setCapturedImg] = useState(null);
  const [isOutFocus, setIsOutFocus] = useState(false);
  const [contentVersion, setContentVersion] = useState(0);
  const [imgCapClked, setImgCapClked] = useState(false);

  useEffect(() => {
    if (Array.isArray(ResultPDF) && ResultPDF.length >= 0) {
      setPdfUploadList((prev) => (prev.length === 0 && ResultPDF.length > 0 ? [...ResultPDF] : prev));
    }
  }, [ResultPDF?.length]);

  useEffect(() => {
    if (!editorRef.current) return;
    const next = EditorRef ?? "";
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
    triggerContentCheck();
  }, [EditorRef]);

  useEffect(() => {
    if (!listenPopUp && editorRef.current) editorRef.current.focus();
  }, [listenPopUp]);

  const listenPopUpClose = useCallback(() => {
    clickKeyRef.current = false;
    setListenPopUp(false);
  }, []);

  const voiceToTextgetter = useCallback((textStorage) => {
    if (!textStorage || !clickKeyRef.current) return;
    setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const textToInsert = ` ${(textStorage || "").trim()}`;
      document.execCommand("insertText", false, textToInsert);
    }, 0);
  }, []);

  const handleStyleChange = (command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
    switch (command) {
      case "bold": setBold((b) => !b); break;
      case "italic": setItalic((i) => !i); break;
      case "underline": setUnderline((u) => !u); break;
      case "strikeThrough": setStrike((s) => !s); break;
      case "insertUnorderedList":
        setBulletList((b) => !b);
        setOrderedList(false);
        break;
      case "insertOrderedList":
        setOrderedList((o) => !o);
        setBulletList(false);
        break;
      case "justifyLeft":
        setJustifyLeft(true); setJustifyCenter(false); setJustifyRight(false); setJustifyFull(false);
        break;
      case "justifyCenter":
        setJustifyLeft(false); setJustifyCenter(true); setJustifyRight(false); setJustifyFull(false);
        break;
      case "justifyRight":
        setJustifyLeft(false); setJustifyCenter(false); setJustifyRight(true); setJustifyFull(false);
        break;
      case "justifyFull":
        setJustifyLeft(false); setJustifyCenter(false); setJustifyRight(false); setJustifyFull(true);
        break;
      default:
        break;
    }
  };

  const handleClearFormatting = () => {
    document.execCommand("removeFormat", false, null);
    document.execCommand("unlink", false, null);
    editorRef.current?.focus();
    setBold(false); setItalic(false); setUnderline(false); setStrike(false);
  };

  const handleInsertHr = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand("insertHTML", false, '<hr/><div><br/></div>');
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    let node = range.startContainer;
    while (node && node !== editor && node.previousSibling?.nodeName !== "HR") {
      if (node.parentElement && node.parentElement !== editor) {
        node = node.parentElement;
      } else {
        break;
      }
    }
    const target = node?.previousSibling?.nodeName === "HR" ? node : null;
    if (target) {
      const r = document.createRange();
      r.setStart(target, 0);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  };

  const getTextStylesAtCursor = useCallback(() => {
    setBold(false); setItalic(false); setUnderline(false); setStrike(false);
    setJustifyLeft(false); setJustifyCenter(false); setJustifyRight(false); setJustifyFull(false);
    setBulletList(false); setOrderedList(false);
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    const node = range.commonAncestorContainer;
    let current = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (current && current !== editorRef.current) {
      if (current.tagName === "UL") { setBulletList(true); break; }
      if (current.tagName === "OL") { setOrderedList(true); break; }
      current = current.parentElement;
    }
    const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    const style = el && window.getComputedStyle(el);
    if (!style) return;
    if (style.fontStyle === "italic" || document.queryCommandState("italic")) setItalic(true);
    if (style.fontWeight === "700" || document.queryCommandState("bold")) setBold(true);
    if (style.textDecoration?.includes("underline") || document.queryCommandState("underline")) setUnderline(true);
    if (style.textDecoration?.includes("line-through") || document.queryCommandState("strikeThrough")) setStrike(true);
    if (style.textAlign === "left") setJustifyLeft(true);
    if (style.textAlign === "center") setJustifyCenter(true);
    if (style.textAlign === "right") setJustifyRight(true);
    if (style.textAlign === "justify") setJustifyFull(true);
  }, []);

  const handlePaste = (e) => {
    e.preventDefault();
    const plainText = e.clipboardData.getData("text/plain");
    const normalized = plainText.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    document.execCommand("insertText", false, normalized);
  };

  const handleKeyDown = (e) => {
    if (addSpaceOnTab && e.key === "Tab") {
      e.preventDefault();
      document.execCommand("insertHTML", false, "\u00A0\u00A0\u00A0\u00A0\u00A0");
    }
  };

  const generateTableHTML = (rows, cols) => {
    let html = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ccc;">';
    for (let i = 0; i < rows; i++) {
      html += "<tr>";
      for (let j = 0; j < cols; j++) {
        html += '<td style="border: 1px solid #ccc; padding: 8px; min-width: 60px; vertical-align: top;">&nbsp;</td>';
      }
      html += "</tr>";
    }
    html += "</table>";
    return html;
  };

  const handleTableInsert = () => {
    document.execCommand("insertHTML", false, generateTableHTML(tableRows, tableCols));
    editorRef.current?.focus();
    setTableMenuAnchor(null);
  };

  const onUploadPDF = (event) => {
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) {
      errorMsg?.("No file selected.");
      return;
    }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp", "image/webp"];
    const maxSize = 3 * 1024 * 1024;
    const finallist = [];
    let processed = 0;
    fileList.forEach((file) => {
      if (!allowed.includes(file.type)) {
        errorMsg?.("Only image files are allowed (JPEG, JPG, PNG, GIF, BMP, WEBP).");
        processed++;
        if (processed === fileList.length && finallist.length > 0) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
        return;
      }
      if (file.size > maxSize) {
        errorMsg?.("File size exceeds 3 MB.");
        processed++;
        if (processed === fileList.length && finallist.length > 0) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const blob = new Blob([e.target.result], { type: file.type });
        finallist.push({ document_file: blob, document_name: file.name });
        processed++;
        if (processed === fileList.length) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
      };
      reader.onerror = () => {
        errorMsg?.("Error reading the file.");
        processed++;
        if (processed === fileList.length && finallist.length > 0) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const closeWebCamPop = () => setImgCapClked(false);

  const dataUrlToBlob = (dataUrl) => {
    try {
      const parts = String(dataUrl).split(",");
      const mime = parts[0].match(/:(.*?);/)?.[1] || "image/jpeg";
      const bstr = atob(parts[1] || "");
      const u8 = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
      return new Blob([u8], { type: mime });
    } catch {
      return null;
    }
  };

  const addImgCapture = (data) => {
    if (!data) return;
    setImgCapClked(false);
    // Convert and append to the shared file list so multiple captures stack.
    let blob = null;
    let docName = `capture_${Date.now()}.jpg`;
    if (typeof data === "string" && data.startsWith("data:")) {
      blob = dataUrlToBlob(data);
    } else if (data instanceof Blob) {
      blob = data;
    } else if (data?.document_file) {
      setPdfUploadList((prev) => [...prev, data]);
      return;
    }
    if (blob) {
      setPdfUploadList((prev) => [...prev, { document_file: blob, document_name: docName }]);
    }
    // Keep the legacy single-image state populated for any downstream code that
    // still reads it; the file list is now the source of truth.
    setCapturedImg(data);
  };

  const disableBtn = isCompleted;
  const rawHtml = editorRef.current?.innerHTML || "";
  const textOnly = rawHtml.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  const hasContent = textOnly.length > 0;
  const canComplete = !isCompleted && (hasContent || pdfUploadList.length > 0 || !!capturedImg);

  const triggerContentCheck = () => setContentVersion((v) => v + 1);

  return (
    <Paper
      component="div"
      className="rad_tst_tmp_content"
      elevation={1}
      sx={{
        height: overallHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box
        component="div"
        borderBottom="1px solid #e0e0e0"
        pl="0.5vw"
        pr="0.5vw"
        sx={{
          minHeight: 40,
          flexShrink: 0,
          backgroundColor: "#fafafa",
          py: "4px",
        }}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={0.5}
        onMouseLeave={() => setIsOutFocus(true)}
        onMouseOver={() => setIsOutFocus(false)}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={600} fontSize="0.85vw" sx={{ mr: 1 }}>{Title}</Typography>
          {(() => {
            const ICON_SX = { fontSize: "1.05rem" };
            const BTN_SX = {
              padding: "4px",
              borderRadius: "4px",
              "&:hover": { backgroundColor: "rgba(4, 183, 177, 0.08)" },
            };
            const BTN_ACTIVE_SX = {
              ...BTN_SX,
              backgroundColor: "rgba(4, 183, 177, 0.12)",
              color: "primary.main",
            };
            const groupSx = { display: "flex", alignItems: "center", gap: "1px" };
            const VDivider = () => <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: "4px" }} />;
            const tbBtn = (label, icon, onClick, active = false, disabled = false) => (
              <Tooltip key={label} title={label} placement="top" arrow>
                <span>
                  <IconButton
                    disabled={EditorDisable || disabled}
                    onClick={onClick}
                    size="small"
                    sx={active ? BTN_ACTIVE_SX : BTN_SX}
                  >
                    {React.cloneElement(icon, { sx: { ...ICON_SX, ...(icon.props?.sx || {}) } })}
                  </IconButton>
                </span>
              </Tooltip>
            );
            return (
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.25} sx={{ visibility: EditorDisable ? "hidden" : "visible" }}>
                <Box sx={groupSx}>
                  {tbBtn("Undo (Ctrl+Z)", <UndoIcon />, () => handleStyleChange("undo"))}
                  {tbBtn("Redo (Ctrl+Y)", <RedoIcon />, () => handleStyleChange("redo"))}
                </Box>
                <VDivider />
                <Box sx={groupSx}>
                  {tbBtn("Bold", <FormatBoldIcon />, () => handleStyleChange("bold"), bold)}
                  {tbBtn("Italic", <FormatItalicIcon />, () => handleStyleChange("italic"), italic)}
                  {tbBtn("Underline", <FormatUnderlinedIcon />, () => handleStyleChange("underline"), underline)}
                  {tbBtn("Strikethrough", <StrikethroughSIcon />, () => handleStyleChange("strikeThrough"), strike)}
                </Box>
                <VDivider />
                <Box sx={groupSx}>
                  {tbBtn("Align Left", <FormatAlignLeftIcon />, () => handleStyleChange("justifyLeft"), justifyLeft)}
                  {tbBtn("Align Center", <FormatAlignCenterIcon />, () => handleStyleChange("justifyCenter"), justifyCenter)}
                  {tbBtn("Align Right", <FormatAlignRightIcon />, () => handleStyleChange("justifyRight"), justifyRight)}
                  {tbBtn("Justify", <FormatAlignJustifyIcon />, () => handleStyleChange("justifyFull"), justifyFull)}
                </Box>
                <VDivider />
                <Box sx={groupSx}>
                  {tbBtn("Bullet List", <FormatListBulletedIcon />, () => handleStyleChange("insertUnorderedList"), bulletList)}
                  {tbBtn("Numbered List", <FormatListNumberedIcon />, () => handleStyleChange("insertOrderedList"), orderedList)}
                  {tbBtn("Decrease Indent", <FormatIndentDecreaseIcon />, () => handleStyleChange("outdent"))}
                  {tbBtn("Increase Indent", <FormatIndentIncreaseIcon />, () => handleStyleChange("indent"))}
                </Box>
                <VDivider />
                <Box sx={groupSx}>
                  {tbBtn("Insert Table", <TableChartIcon />, (e) => setTableMenuAnchor(e.currentTarget))}
                  {tbBtn("Horizontal Line", <HorizontalRuleIcon />, handleInsertHr)}
                  {tbBtn("Clear Formatting", <FormatClearIcon />, handleClearFormatting)}
                </Box>
              </Box>
            );
          })()}
        </Box>
        <Tooltip title="Voice Input" placement="top" arrow>
          <span>
            <IconButton
              disabled={EditorDisable}
              onClick={() => { clickKeyRef.current = true; setListenPopUp(true); }}
              size="small"
              sx={{
                visibility: EditorDisable ? "hidden" : "visible",
                padding: "4px",
                borderRadius: "4px",
                color: "primary.main",
                "&:hover": { backgroundColor: "rgba(4, 183, 177, 0.12)" },
              }}
            >
              <MicIcon sx={{ fontSize: "1.05rem" }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Menu anchorEl={tableMenuAnchor} open={Boolean(tableMenuAnchor)} onClose={() => setTableMenuAnchor(null)} PaperProps={{ style: { padding: 16, minWidth: 200 } }}>
        <MenuItem disabled><Typography variant="subtitle1" fontWeight="bold">Insert Table</Typography></MenuItem>
        <MenuItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}><Typography variant="body2">Rows:</Typography></Grid>
            <Grid item xs={6}>
              <TextField type="number" size="small" value={tableRows} onChange={(e) => setTableRows(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))} inputProps={{ min: 1, max: 10 }} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}><Typography variant="body2">Columns:</Typography></Grid>
            <Grid item xs={6}>
              <TextField type="number" size="small" value={tableCols} onChange={(e) => setTableCols(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)))} inputProps={{ min: 1, max: 8 }} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem onClick={handleTableInsert}>
          <Button variant="contained" fullWidth>Insert Table ({tableRows}x{tableCols})</Button>
        </MenuItem>
      </Menu>

      <Box component="div" borderBottom="1px solid lightgray" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          ref={editorRef}
          component="div"
          contentEditable={!EditorDisable}
          className="editor"
          onMouseUp={getTextStylesAtCursor}
          onSelect={getTextStylesAtCursor}
          onPaste={handlePaste}
          onKeyDown={addSpaceOnTab ? handleKeyDown : undefined}
          onFocus={triggerContentCheck}
          onInput={triggerContentCheck}
          sx={{
            flex: 1,
            height: EditorHeight,
            minHeight: 120,
            overflow: "scroll",
            outline: "none",
            padding: "8px",
            "& ul, & ol": { margin: 0, paddingLeft: "24px" },
            "& li": { marginBottom: "2px" },
            "& table": { borderCollapse: "collapse", width: "100%", margin: "10px 0", border: "1px solid #ccc" },
            "& td": { border: "1px solid #ccc", padding: "8px", minWidth: "60px", verticalAlign: "top" },
            "& th": { border: "1px solid #ccc", padding: "8px", backgroundColor: "#f5f5f5", fontWeight: "bold", textAlign: "left" },
          }}
        />
      </Box>

      {FromTestReport ? (
        !viewOnly ? (
        <Box display="flex" justifyContent="space-between" alignItems="center" flexShrink={0} sx={{ margin: "0.3vh", minHeight: 48, paddingBottom: "0.5vh" }}>
          {!hideSaveAndPostButtons && (
          <Box sx={{ marginLeft: "0.5vw", display: "flex", alignItems: "center", gap: "0.5vw" }}>
            <input type="file" accept="image/*" ref={uploadPDFRef} id="newToolsFieldPdfInput" style={{ display: "none" }} onChange={onUploadPDF} multiple />
            <Box>
              <Button size="small" variant="contained" disabled={disableBtn || EditorDisable} onClick={() => uploadPDFRef.current?.click()}>Upload Image</Button>
              <Button size="small"  variant="contained"  disabled={disableBtn || EditorDisable} onClick={() => setImgCapClked(true)} sx={{ ml: 0.5 }} title="Capture from camera">
                Capture Image
              </Button>
            </Box>
          </Box>
          )}
          <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ flex: 1 }}>
            {!hideSaveAndPostButtons && (
            <Button className="eMed_imgCap_btn" disabled={isCompleted || EditorDisable} variant="outlined" sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32, mr: "1vw" }} onClick={() => { if (editorRef.current) editorRef.current.innerHTML = ""; setPdfUploadList([]); setCapturedImg(null); }}>Clear</Button>
            )}
            {hideSaveAndPostButtons && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", marginRight: "2vw" }}>Sended For Approval</Typography>
            )}
            <Box display="flex" alignItems="center" gap={0.5}>
              {!hideSaveAndPostButtons && !showOnlyApproveButton && (
                <Button disabled={isCompleted || EditorDisable} variant="contained" sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32 }} onClick={() => handleSaveResult?.(removeEmptyStyleTagsAndReplaceSpan(editorRef.current?.innerHTML ?? ""), true, false, pdfUploadList, capturedImg)}>Save</Button>
              )}
              {!hideSaveAndPostButtons && (
                <Button disabled={!canComplete || EditorDisable} variant="contained" sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32 }} onClick={() => handlePostResult?.(removeEmptyStyleTagsAndReplaceSpan(editorRef.current?.innerHTML ?? ""), false, true, pdfUploadList, capturedImg)}>{postButtonLabel}</Button>
              )}
            </Box>
          </Box>
        </Box>
        ) : null
      ) : (
        <Box display="flex" justifyContent="flex-end" alignItems="center" height="8vh" sx={{ margin: "0.3vh" }}>
          <Button variant="contained" sx={{ textTransform: "capitalize", height: "2vw", mr: "1vw" }} disabled={EditorDisable} onClick={() => handlePostResult?.(removeEmptyStyleTagsAndReplaceSpan(editorRef.current?.innerHTML ?? ""))}>Complete</Button>
        </Box>
      )}

      {(pdfUploadList?.length > 0 || capturedImg) ? (
        <Box sx={{ display: "flex", flexDirection: "column", maxHeight: 150, overflowY: "auto", padding: "5px", flexShrink: 0 }}>
          {pdfUploadList?.length > 0 ? (
            pdfUploadList.map((fileDetails, index) => (
              <Box key={index} display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, padding: "2px 0" }}>
                <Typography color="#04B7B1" sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
                  📄 {fileDetails?.document_name?.length > 23 ? fileDetails.document_name.slice(0, 20) + "..." : (fileDetails?.document_name ?? "image.jpg")}
                </Typography>
                {(isCompleted || EditorDisable) ? null : (
                  <IconButton size="small" onClick={() => setPdfUploadList((prev) => prev.filter((_, i) => i !== index))} sx={{ padding: "4px", flexShrink: 0 }} title="Clear image">✕</IconButton>
                )}
              </Box>
            ))
          ) : capturedImg ? (
            <Box display="flex" alignItems="center" gap={0.5} flexShrink={0} sx={{ padding: "2px 0" }}>
              <Typography color="#04B7B1">📄 {typeof capturedImg === "string" ? "image.jpg" : (capturedImg?.document_name ?? "image.jpg")}</Typography>
              {(isCompleted || EditorDisable) ? null : (
                <IconButton size="small" onClick={() => setCapturedImg(null)} sx={{ flexShrink: 0 }} title="Clear image">✕</IconButton>
              )}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {imgCapClked && (
        <OpenWebCamPopUP
          capturedImg={typeof capturedImg === "string" ? capturedImg : null}
          closePopup={closeWebCamPop}
          addImgCapture={addImgCapture}
        />
      )}

      {listenPopUp && (
        <VoiceToText voiceTextConverter={voiceToTextgetter} listenPopUp={listenPopUpClose} micTextEditDisabled={micTextEditDisabled} />
      )}
    </Paper>
  );
}
